import React from 'react';
import { motion } from 'framer-motion';
import { FiBarChart2, FiFileText, FiZap } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const cards = [
  {
    icon: FiBarChart2,
    title: 'Call Analytics Dashboard',
    items: [
      'Total calls handled (inbound + outbound)',
      'Resolution rate — % resolved without human escalation',
      'Average handle time per call',
      'Peak call hours and traffic patterns',
      'Escalation reasons and frequency',
      'Week-over-week performance trends',
    ],
  },
  {
    icon: FiFileText,
    title: 'Call Recordings & Transcripts',
    items: [
      'Every conversation recorded and transcribed',
      'Searchable transcript library',
      'Flag and review specific calls',
      'Use recordings for team training and QA',
      'Compliance-ready documentation and audit trail',
    ],
  },
  {
    icon: FiZap,
    title: 'Actionable Insights',
    items: [
      'Most common customer issues — ranked by frequency',
      'FAQ gaps — questions not yet in your knowledge base',
      'Staffing recommendations: AI vs. human thresholds',
      'Customer sentiment patterns over time',
      'Product feedback extracted directly from call data',
    ],
  },
];

const Visibility = () => (
  <section className="py-12 md:py-16 bg-white border-t border-slate-100">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        className="text-center mb-10 md:mb-12"
      >
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Full Visibility Into Every Call</h2>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          Most businesses have no idea what happens on their support calls. With Valtivo, you see everything.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-10">
        {cards.map((card, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="bg-slate-50 rounded-2xl p-6 border border-slate-200 hover:border-green-200 hover:shadow-lg transition-all duration-300 group"
          >
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-5 border border-slate-200 group-hover:bg-green-50 group-hover:border-green-100 transition-colors shadow-sm">
              <SafeIcon icon={card.icon} className="w-6 h-6 text-slate-700 group-hover:text-green-700 transition-colors" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-4">{card.title}</h3>
            <ul className="space-y-2.5">
              {card.items.map((item, j) => (
                <li key={j} className="flex items-start gap-2.5 text-sm text-slate-600 leading-snug">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0 mt-1.5"></div>
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        className="bg-slate-900 rounded-2xl p-6 md:p-8 text-center max-w-4xl mx-auto"
      >
        <p className="text-white text-base md:text-lg leading-relaxed">
          Your AI agent doesn't just handle calls — it tells you what your customers are actually calling about, what's broken, and where to improve.{' '}
          <span className="text-green-400 font-semibold">That intelligence alone is worth the investment.</span>
        </p>
      </motion.div>
    </div>
  </section>
);

export default Visibility;